/* eslint-disable no-unused-vars */
import { useCallback, useState } from 'react'
import { useNavigate, NavigateFunction } from 'react-router-dom'
import moment from 'moment'

import { t } from '@/i18n'
import {
  useProposalQuery,
  DealsProposalCurrencyChoices,
} from '@/apollo/graphql'
import { IProposal, TFormSubmit } from '@/types'
import { useAPI, useToast } from '@/hooks/index'
import { homePath, transformData } from '@/utils/index'

interface IUseData {
  id?: string
}

const defaultData: Partial<IProposal & { installations?: any[] }> = {
  currency: DealsProposalCurrencyChoices.Ils,
  installations: [],
}

export const useData = ({ id }: IUseData) => {
  const navigate: NavigateFunction = useNavigate()
  const addToast = useToast()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<any>(defaultData)

  const { loading, refetch } = useProposalQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.proposal) {
        // Transform installations from GraphQL edges/nodes structure to flat array
        // Also map GraphQL field names to form field names
        const installations =
          res.proposal.installations?.edges
            ?.map(edge => edge?.node)
            .filter(Boolean)
            .map(installation => ({
              id: installation?.id,
              name: installation?.name,
              price_without_vat: installation?.priceWithoutVat,
              installation_type: installation?.installationType,
              is_customer_paid: installation?.isCustomerPaid,
            })) || []

        setData({
          ...res.proposal,
          currency: res.proposal.currency || DealsProposalCurrencyChoices.Ils,
          installations,
        })
      }
    },
  })

  const onSubmit: TFormSubmit<IProposal> = useCallback(
    (path, isEdit, callback) => d => {
      if (d.validTo) {
        d.validTo = moment(d.validTo).format('YYYY-MM-DD')
      }

      sendForm(
        {
          path,
          method: isEdit ? 'PUT' : 'POST',
          data: transformData(d),
          callback: e => {
            callback?.(e)
            refetch()
          },
        },
        { showNotify: true },
      )
    },
    [refetch, sendForm],
  )

  const onDelete = useCallback(
    (id: string) => {
      sendForm(
        {
          path: `deals/proposal/${id}/`,
          method: 'DELETE',
          data: undefined,
          callback: () => {
            addToast('success', t('success'))
            navigate(-1)
          },
        },
        {
          showNotify: false,
        },
      )
    },
    [addToast, navigate, sendForm],
  )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  return {
    loading,
    submitting,
    data,
    onSubmit,
    onCancel,
    onDelete,
  }
}
